import {
  getDetailsStepErrors,
  PROPOSAL_FORM_PERSIST_VERSION,
  type ProposalFormState,
} from '@/stores/proposal-form';

export type ProposalCreationFlow = 'manual' | 'generate';

export interface ProposalAuthContinuation {
  readonly intent: 'create-proposal';
  readonly schemaVersion: 1;
  readonly draftSchemaVersion: typeof PROPOSAL_FORM_PERSIST_VERSION;
  readonly flow: ProposalCreationFlow;
  readonly targetStep: 4;
  readonly draftRevision: number;
  readonly createdAt: number;
  readonly expiresAt: number;
}

export type ProposalAuthContinuationFailureReason =
  | 'corrupt'
  | 'expired'
  | 'draft_missing'
  | 'draft_changed'
  | 'draft_incompatible'
  | 'storage_unavailable';

export type ProposalAuthContinuationReadResult =
  | { readonly status: 'none' }
  | { readonly status: 'ready'; readonly continuation: ProposalAuthContinuation }
  | {
      readonly status: 'stale';
      readonly reason: ProposalAuthContinuationFailureReason;
      readonly flow: ProposalCreationFlow | 'unknown';
      readonly createdAt: number | null;
    };

const STORAGE_KEY = 'morfi-proposal-create-auth-continuation';
export const PROPOSAL_AUTH_CONTINUATION_TTL_MS: number = 30 * 60 * 1_000;

function getSessionStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    // SILENT-CATCH: privacy modes may deny storage access; callers handle null safely.
    return null;
  }
}

export function createProposalAuthContinuation(
  flow: ProposalCreationFlow,
  draftRevision: number,
  now: number = Date.now(),
): ProposalAuthContinuation {
  return {
    intent: 'create-proposal',
    schemaVersion: 1,
    draftSchemaVersion: PROPOSAL_FORM_PERSIST_VERSION,
    flow,
    targetStep: 4,
    draftRevision,
    createdAt: now,
    expiresAt: now + PROPOSAL_AUTH_CONTINUATION_TTL_MS,
  };
}

export function validateProposalAuthContinuationDraft(
  continuation: ProposalAuthContinuation,
  draft: ProposalFormState,
  requireLocation: boolean,
): ProposalAuthContinuationFailureReason | null {
  if (draft.title.trim().length === 0) return 'draft_missing';
  if (draft.lastTouchedAt !== continuation.draftRevision) return 'draft_changed';
  if (
    draft.outcomes.length < 2 ||
    draft.coverImageUploading ||
    draft.outcomes.some((outcome) => outcome.imageUploading) ||
    Object.keys(getDetailsStepErrors(draft, { requireLocation })).length > 0
  ) {
    return 'draft_incompatible';
  }
  return null;
}

export function writeProposalAuthContinuation(continuation: ProposalAuthContinuation): boolean {
  const storage = getSessionStorage();
  if (storage === null) return false;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(continuation));
    return true;
  } catch {
    // SILENT-CATCH: the caller surfaces the bounded storage-unavailable state.
    return false;
  }
}

export function clearProposalAuthContinuation(): void {
  const storage = getSessionStorage();
  if (storage === null) return;
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // SILENT-CATCH: unavailable session storage is already a safe cleared state.
  }
}

export function readProposalAuthContinuation(
  now: number = Date.now(),
): ProposalAuthContinuationReadResult {
  const storage = getSessionStorage();
  if (storage === null) return { status: 'none' };

  let raw: string | null;
  try {
    raw = storage.getItem(STORAGE_KEY);
  } catch {
    // SILENT-CATCH: this function returns a typed, observable failure result.
    return {
      status: 'stale',
      reason: 'storage_unavailable',
      flow: 'unknown',
      createdAt: null,
    };
  }
  if (raw === null) return { status: 'none' };

  // ALLOW-UNKNOWN: sessionStorage JSON is untrusted until the guard below validates it.
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // SILENT-CATCH: corrupt JSON is cleared and returned as a typed failure result.
    clearProposalAuthContinuation();
    return { status: 'stale', reason: 'corrupt', flow: 'unknown', createdAt: null };
  }
  if (!isProposalAuthContinuation(parsed)) {
    clearProposalAuthContinuation();
    return { status: 'stale', reason: 'corrupt', flow: 'unknown', createdAt: null };
  }
  if (parsed.expiresAt <= now) {
    clearProposalAuthContinuation();
    return {
      status: 'stale',
      reason: 'expired',
      flow: parsed.flow,
      createdAt: parsed.createdAt,
    };
  }
  return { status: 'ready', continuation: parsed };
}

// ALLOW-UNKNOWN: type guard accepts the untrusted JSON boundary and narrows it field by field.
function isProposalAuthContinuation(value: unknown): value is ProposalAuthContinuation {
  if (value === null || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record);
  return (
    keys.length === 8 &&
    record.intent === 'create-proposal' &&
    record.schemaVersion === 1 &&
    record.draftSchemaVersion === PROPOSAL_FORM_PERSIST_VERSION &&
    (record.flow === 'manual' || record.flow === 'generate') &&
    record.targetStep === 4 &&
    Number.isSafeInteger(record.draftRevision) &&
    typeof record.draftRevision === 'number' &&
    record.draftRevision >= 0 &&
    Number.isSafeInteger(record.createdAt) &&
    typeof record.createdAt === 'number' &&
    Number.isSafeInteger(record.expiresAt) &&
    typeof record.expiresAt === 'number' &&
    record.createdAt >= 0 &&
    record.expiresAt - record.createdAt === PROPOSAL_AUTH_CONTINUATION_TTL_MS
  );
}
